// What the Drive watcher has merged, across every folder in drive_watch_root.
//
// Luyanda shared the insurance folder with the service account on 8 September
// and the watcher pulled 33 files within the hour. Two more folders followed on
// 9 September, telematics and maintenance. Before this screen the only way to
// know any of that had happened was to query the database.
//
// THE COLUMN THAT MATTERS MOST IS "lines". Reconciling to a total printed on the
// document is the gate for a machine confirming anything on its own, and all
// seventeen MiWay files passed that gate having read exactly one vehicle line
// each. One line that ties to a one-line total is arithmetically perfect and
// factually wrong, because those schedules carry thirty cars. So this screen
// puts the line count next to the tick and marks a short read in red, rather
// than showing a green "reconciled" that would invite somebody to confirm it.
//
// The reader was rebuilt on 9 September and those same seventeen files now read
// twenty nine and thirty vehicles each. The short-read guard stays: it is what
// caught the defect, and it costs nothing when there is nothing to catch.
import { useEffect, useMemo, useState } from 'react'
import { CloudDownload, AlertTriangle, ChevronRight } from 'lucide-react'
import { useGetDriveWatch } from '../../hooks/backend/statements'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { formatZar, formatInt } from '../../utils/statements'

type FileRow = {
  upload_id: string
  drive_folder: string
  filename: string
  state: string
  auto_confirmed: boolean
  supplier: string | null
  document_number: string | null
  schedule_date: string | null
  covers_from: string | null
  stated_total_zar: number | null
  reader_confidence: string | null
  lines_found: number
  lines_matched: number
  what_happened: string
  uploaded_at: string | null
  suspect_short_read: boolean
}

type Summary = {
  files: number
  read: number
  confirmed: number
  auto_confirmed: number
  reconciled: number
  waiting_for_a_person: number
  suspect_short_read: number
  folders: number
  latest_pull: string | null
}

function monthOf(d: string | null): string {
  if (!d) return '—'
  const [y, m] = d.split('-')
  if (!y || !m) return d
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const i = Number(m) - 1
  return i >= 0 && i < 12 ? `${names[i]} ${y}` : d
}

function whenOf(ts: string | null): string {
  if (!ts) return '—'
  return ts.slice(0, 16).replace('T', ' ')
}

export function DriveWatch() {
  const watch = useGetDriveWatch<{ summary: Summary | null; files: FileRow[] }>()
  const [open, setOpen] = useState(false)
  const [folder, setFolder] = useState<string>('')

  const summary = watch.data?.summary ?? null
  const files = useMemo(() => watch.data?.files ?? [], [watch.data])

  const folders = useMemo(
    () => Array.from(new Set(files.map((f) => f.drive_folder))).sort(),
    [files],
  )
  const shown = useMemo(
    () => (folder ? files.filter((f) => f.drive_folder === folder) : files),
    [files, folder],
  )

  useEffect(() => {
    if (folder && !folders.includes(folder)) setFolder('')
  }, [folder, folders])

  if (watch.loading && !summary) {
    return (
      <div className="flex items-center gap-2 border-b border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" />
        Reading what the Drive watcher has merged
      </div>
    )
  }

  if (!summary || summary.files === 0) {
    return (
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <CloudDownload className="h-4 w-4 text-muted-foreground" />
          Merged from Drive
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Nothing pulled yet. The watcher runs every fifteen minutes across every folder in
          drive_watch_root, and can only see a folder once it is shared read-only with
          fleetly-calendar@fleetly-ops.iam.gserviceaccount.com. Sharing with the fleetly.co.za
          domain does not reach the service account: it has to be named directly.
        </p>
      </div>
    )
  }

  return (
    <div className="border-b border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[44px] w-full items-center gap-3 px-4 py-3 text-left"
      >
        <CloudDownload className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Merged from Drive</span>
        <span className="text-xs text-muted-foreground">
          {formatInt(summary.files)} file{summary.files === 1 ? '' : 's'} from{' '}
          {formatInt(summary.folders)} folder{summary.folders === 1 ? '' : 's'}, last pull{' '}
          {whenOf(summary.latest_pull)}
        </span>
        <span className="ml-auto flex items-center gap-3">
          {summary.suspect_short_read > 0 && (
            <span className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {formatInt(summary.suspect_short_read)} short read
            </span>
          )}
          {summary.waiting_for_a_person > 0 && (
            <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              {formatInt(summary.waiting_for_a_person)} waiting for a person
            </span>
          )}
          {summary.auto_confirmed > 0 && (
            <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              {formatInt(summary.auto_confirmed)} confirmed on their own
            </span>
          )}
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
          />
        </span>
      </button>

      {open && (
        <div className="border-t border-border">
          {summary.suspect_short_read > 0 && (
            <p className="border-b border-border bg-red-50 px-4 py-2 text-xs text-red-800">
              {formatInt(summary.suspect_short_read)} file
              {summary.suspect_short_read === 1 ? '' : 's'} tie to a total printed on the document
              having read fewer than five vehicle lines. The arithmetic is consistent and the
              reading is not: a fleet schedule with one line on it has not been read properly.
              Do not confirm these until the reader finds the whole table.
            </p>
          )}

          {folders.length > 1 && (
            <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2">
              <button
                type="button"
                onClick={() => setFolder('')}
                className={cn(
                  'rounded-md px-2 py-1 text-xs',
                  folder === '' ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
                )}
              >
                All
              </button>
              {folders.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFolder(f)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs',
                    folder === f ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">File</th>
                  <th className="px-3 py-2 font-medium">Covers</th>
                  <th className="px-3 py-2 text-right font-medium">Lines</th>
                  <th className="px-3 py-2 text-right font-medium">Matched</th>
                  <th className="px-3 py-2 text-right font-medium">Ties to</th>
                  <th className="px-4 py-2 font-medium">What happened</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((f) => (
                  <tr
                    key={f.upload_id}
                    className={cn('border-b border-border/60', f.suspect_short_read && 'bg-red-50/50')}
                  >
                    <td className="px-4 py-2">
                      <div className="truncate font-medium text-foreground" title={f.filename}>
                        {f.filename}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {f.supplier ?? 'supplier not read'}
                        {f.document_number ? ` · ${f.document_number}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {monthOf(f.covers_from)}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        f.suspect_short_read ? 'font-semibold text-red-700' : 'text-foreground',
                      )}
                    >
                      {formatInt(f.lines_found)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatInt(f.lines_matched)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {f.stated_total_zar === null ? 'no printed total' : formatZar(f.stated_total_zar)}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{f.what_happened}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
